import { format } from 'date-fns';
import { EMPTY_VALUE, REPORT_FINALIDADE_MAX_LENGTH } from '@/constants';
import { formatDateOnly } from '@/lib/date';
import { getAge } from '@/utils';
import { translateGender } from '@/utils/translateGender';
import { educationLevelMap } from '../patients/constants';
import { SECTIONS } from '../wizard/constants';
import EditableText from './EditableText';
import type { Psychologist, ReportData } from './types';

interface PatientInfoTableProps {
    data: ReportData;
    onUpdate: (updates: Partial<ReportData>) => void;
    disabled?: boolean;
    psychologist?: Psychologist | null;
    /** Same escape hatch as `ReportSection`: the hidden measurer renders a
     *  second copy of this table and must not steal the anchor id. */
    idPrefix?: string;
}

export function PatientInfoTable({
    data,
    onUpdate,
    disabled,
    psychologist,
    idPrefix = '',
}: PatientInfoTableProps) {
    const { patient } = data;
    const maxDate = format(new Date(), 'yyyy-MM-dd');
    const educationLevelValue = patient?.education_level
        ? (educationLevelMap[patient.education_level] ?? patient.education_level)
        : undefined;

    return (
        <div id={`${idPrefix}section-${SECTIONS.PATIENT}`} className="mb-6">
            <table className="w-full border-collapse border border-slate-300 bg-white text-xs">
                <tbody>
                    <tr>
                        <td className="w-1/6 border border-slate-300 bg-slate-50 p-2 font-bold">
                            Nome:
                        </td>
                        <td className="border border-slate-300 p-2 text-slate-700">
                            {patient?.full_name || EMPTY_VALUE}
                        </td>
                        <td className="w-1/6 border border-slate-300 bg-slate-50 p-2 font-bold">
                            Sexo:
                        </td>
                        <td className="border border-slate-300 p-2 text-slate-700">
                            {translateGender(patient?.gender)}
                        </td>
                        <td className="w-1/6 border border-slate-300 bg-slate-50 p-2 font-bold">
                            Idade:
                        </td>
                        <td className="border border-slate-300 p-2 text-slate-700">
                            {getAge(patient?.date_of_birth) || EMPTY_VALUE}
                        </td>
                    </tr>

                    <tr>
                        <td className="border border-slate-300 bg-slate-50 p-2 font-bold">
                            Data Nasc:
                        </td>
                        <td className="border border-slate-300 p-2 text-slate-700">
                            {patient?.date_of_birth
                                ? formatDateOnly(patient.date_of_birth)
                                : EMPTY_VALUE}
                        </td>
                        <td
                            className="border border-slate-300 bg-slate-50 p-2 font-bold"
                            colSpan={2}
                        >
                            Escolaridade:
                        </td>
                        <td className="border border-slate-300 p-2" colSpan={2}>
                            <EditableText
                                value={educationLevelValue}
                                onSave={(value) =>
                                    onUpdate({
                                        patient: {
                                            ...patient,
                                            education_level: value as NonNullable<
                                                ReportData['patient']
                                            >['education_level'],
                                        },
                                    } as Partial<ReportData>)
                                }
                                placeholder="Escolaridade..."
                                disabled={disabled}
                            />
                        </td>
                    </tr>
                    <tr>
                        <td className="border border-slate-300 bg-slate-50 p-2 font-bold">
                            Solicitante:
                        </td>
                        <td className="border border-slate-300 p-2" colSpan={5}>
                            <EditableText
                                value={patient?.referring_professional}
                                onSave={(value) =>
                                    onUpdate({
                                        patient: {
                                            ...patient,
                                            referring_professional: value,
                                        },
                                    } as Partial<ReportData>)
                                }
                                placeholder="Profissional responsável..."
                                disabled={disabled}
                            />
                        </td>
                    </tr>
                    <tr>
                        <td className="border border-slate-300 bg-slate-50 p-2 font-bold">
                            Finalidade:
                        </td>
                        <td className="border border-slate-300 p-2" colSpan={5}>
                            <EditableText
                                value={data.finalidade}
                                onSave={(value) => onUpdate({ finalidade: value })}
                                placeholder="Finalidade da avaliação..."
                                maxLength={REPORT_FINALIDADE_MAX_LENGTH}
                                disabled={disabled}
                            />
                        </td>
                    </tr>
                    <tr>
                        <td className="border border-slate-300 bg-slate-50 p-2 font-bold">
                            Autor(a)
                        </td>
                        <td className="border border-slate-300 p-2 text-slate-700" colSpan={5}>
                            {(() => {
                                const parts: string[] = [];
                                if (psychologist?.full_name) {
                                    parts.push(psychologist.full_name);
                                }
                                if (psychologist?.crp) {
                                    parts.push(`CRP ${psychologist.crp}`);
                                }
                                if (psychologist?.email) {
                                    parts.push(psychologist.email);
                                }
                                return parts.length > 0 ? parts.join(' | ') : EMPTY_VALUE;
                            })()}
                        </td>
                    </tr>
                    <tr>
                        <td className="border border-slate-300 bg-slate-50 p-2 font-bold">
                            Período de Avaliação
                        </td>
                        <td className="border border-slate-300 p-2" colSpan={5}>
                            <div className="flex items-center gap-3">
                                <EditableText
                                    type="date"
                                    max={maxDate}
                                    className="m-0 pr-2"
                                    value={data?.tests_performed_at_start?.split('T')[0]}
                                    displayValue={
                                        data.tests_performed_at_start
                                            ? formatDateOnly(data.tests_performed_at_start)
                                            : ''
                                    }
                                    onSave={(value: string) =>
                                        onUpdate({
                                            tests_performed_at_start: value,
                                        })
                                    }
                                    placeholder="Início..."
                                    disabled={disabled}
                                />
                                <span className="shrink-0 text-center">até</span>
                                <EditableText
                                    type="date"
                                    max={maxDate}
                                    className="m-0 pr-2"
                                    value={data?.tests_performed_at_end?.split('T')[0]}
                                    displayValue={
                                        data.tests_performed_at_end
                                            ? formatDateOnly(data.tests_performed_at_end)
                                            : ''
                                    }
                                    onSave={(value: string) =>
                                        onUpdate({
                                            tests_performed_at_end: value,
                                        })
                                    }
                                    placeholder="Fim..."
                                    disabled={disabled}
                                />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}
